import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldError, fieldErrorProps } from '@/components/ui/field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAffiliatesApi } from '@/hooks/useAffiliatesApi';
import { type FieldErrors, getFieldError } from '@/lib/validationErrors';
import { ApiError, toApiError } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Ban,
  CheckCircle,
  Loader2,
  Mail,
  Plus,
  Settings2,
  Shield,
  Trash2,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { useEffect } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';

const roleLabels: Record<string, string> = {
  affiliate_owner: 'Owner',
  affiliate_coach: 'Coach',
};

const roleColors: Record<string, string> = {
  affiliate_owner: 'bg-green-500/10 text-green-500 border-green-500/20',
  affiliate_coach: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
};

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  invited: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  suspended: 'bg-red-500/10 text-red-500 border-red-500/20',
};

interface TeamMember {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: 'affiliate_owner' | 'affiliate_coach';
  is_active: boolean;
  invite_status: 'invited' | 'active' | 'suspended';
  created_at: string;
}

interface CoachPermissions {
  can_invite: boolean;
  can_create_events: boolean;
  can_view_billing: boolean;
  can_manage_coaches: boolean;
}

export default function AffiliateTeam() {
  const affiliatesApi = useAffiliatesApi();
  const queryClient = useQueryClient();

  // Modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteErrors, setInviteErrors] = useState<FieldErrors>();

  // FEAT-APR08-007 — multi-gym owners pick which gym the coach joins.
  const profiles = useAuthStore((s) => s.profiles);
  const ownedGyms = profiles
    .filter(
      (p) =>
        p.profile_type === 'affiliate_member' && p.member_role === 'owner' && p.context?.affiliate
    )
    .map((p) => ({ id: p.affiliate_id as string, name: p.context?.affiliate?.name ?? 'Gym' }));
  const isMultiGymOwner = ownedGyms.length > 1;
  const [inviteAffiliateId, setInviteAffiliateId] = useState<string>(ownedGyms[0]?.id ?? '');

  // Permissions modal state
  const [permissionsCoach, setPermissionsCoach] = useState<TeamMember | null>(null);
  const [localPermissions, setLocalPermissions] = useState<CoachPermissions>({
    can_invite: false,
    can_create_events: false,
    can_view_billing: false,
    can_manage_coaches: false,
  });

  // Fetch team data
  const { data, isLoading, error } = useQuery({
    queryKey: ['affiliate-team'],
    queryFn: async () => {
      const response = await affiliatesApi.getTeam();
      if (!response.success) throw toApiError(response, 'Failed to load team');
      return response.data;
    },
  });

  // Invite coach mutation
  const inviteMutation = useMutation({
    mutationFn: async ({
      email,
      name,
      affiliateId,
    }: {
      email: string;
      name?: string;
      affiliateId?: string;
    }) => {
      const response = await affiliatesApi.inviteCoach(email, name, affiliateId);
      if (!response.success) throw toApiError(response, 'Failed to send invitation');
      return response.data;
    },
    onSuccess: () => {
      toast.success('Coach invitation sent!');
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      setInviteErrors(undefined);
      queryClient.invalidateQueries({ queryKey: ['affiliate-team'] });
    },
    onError: (error) => {
      if (
        error instanceof ApiError &&
        error.details &&
        (getFieldError(error.details, 'email') || getFieldError(error.details, 'name'))
      ) {
        setInviteErrors(error.details);
        const fieldId = getFieldError(error.details, 'email') ? 'invite-email' : 'invite-name';
        requestAnimationFrame(() => document.getElementById(fieldId)?.focus());
        return;
      }
      toast.error(error.message || 'Failed to send invitation');
    },
  });

  // Toggle access mutation
  const toggleAccessMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      const response = await affiliatesApi.updateCoachAccess(userId, isActive);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: (_, { isActive }) => {
      toast.success(isActive ? 'Coach access restored' : 'Coach access suspended');
      queryClient.invalidateQueries({ queryKey: ['affiliate-team'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update access');
    },
  });

  // Remove coach mutation
  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await affiliatesApi.removeCoach(userId);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Coach removed from team');
      queryClient.invalidateQueries({ queryKey: ['affiliate-team'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove coach');
    },
  });

  // Fetch permissions when a coach is selected
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['coach-permissions', permissionsCoach?.id],
    queryFn: async () => {
      if (!permissionsCoach) return null;
      const response = await affiliatesApi.getCoachPermissions(permissionsCoach.id);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!permissionsCoach,
  });

  // Sync fetched permissions into local state when data arrives
  useEffect(() => {
    if (permissionsData?.permissions) {
      setLocalPermissions(permissionsData.permissions);
    }
  }, [permissionsData]);

  // Update permissions mutation
  const updatePermissionsMutation = useMutation({
    mutationFn: async ({
      memberId,
      permissions,
    }: { memberId: string; permissions: CoachPermissions }) => {
      const response = await affiliatesApi.updateCoachPermissions(memberId, permissions);
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Permissions updated');
      setPermissionsCoach(null);
      queryClient.invalidateQueries({ queryKey: ['affiliate-team'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update permissions');
    },
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    inviteMutation.mutate({
      email: inviteEmail,
      name: inviteName || undefined,
      affiliateId: isMultiGymOwner ? inviteAffiliateId || undefined : undefined,
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <p className="text-destructive">{error.message || 'Failed to load team'}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const team = data?.team || [];
  const stats = data?.stats || { total: 0, active: 0, invited: 0 };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">My Team</h1>
          <p className="mt-1 text-muted-foreground">Manage your coaches and staff access</p>
        </div>
        <Button onClick={() => setShowInviteModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Invite Coach
        </Button>
      </div>

      {/* Stats */}
      <div className="mb-8 grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Users className="h-4 w-4" />
              Total Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <UserCheck className="h-4 w-4 text-green-500" />
              Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-500">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Mail className="h-4 w-4 text-blue-500" />
              Pending Invites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-500">{stats.invited}</p>
          </CardContent>
        </Card>
      </div>

      {/* Team Table */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>Coaches and staff with access to your gym</CardDescription>
        </CardHeader>
        <CardContent>
          {team.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No team members yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Invite coaches to help manage your gym
              </p>
              <Button className="mt-4" onClick={() => setShowInviteModal(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Invite Your First Coach
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {team.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell className="text-muted-foreground">{member.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={roleColors[member.role] || ''}>
                        <Shield className="mr-1 h-3 w-3" />
                        {roleLabels[member.role] || member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColors[member.invite_status] || ''}>
                        {member.invite_status === 'active' && (
                          <CheckCircle className="mr-1 h-3 w-3" />
                        )}
                        {member.invite_status === 'invited' && <Mail className="mr-1 h-3 w-3" />}
                        {member.invite_status === 'suspended' && <Ban className="mr-1 h-3 w-3" />}
                        {member.invite_status.charAt(0).toUpperCase() +
                          member.invite_status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {member.role !== 'affiliate_owner' && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Manage permissions"
                            onClick={() => {
                              setPermissionsCoach(member);
                              setLocalPermissions({
                                can_invite: false,
                                can_create_events: false,
                                can_view_billing: false,
                                can_manage_coaches: false,
                              });
                            }}
                          >
                            <Settings2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          {member.is_active ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                toggleAccessMutation.mutate({
                                  userId: member.user_id,
                                  isActive: false,
                                })
                              }
                              disabled={toggleAccessMutation.isPending}
                            >
                              <Ban className="h-4 w-4 text-orange-500" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                toggleAccessMutation.mutate({
                                  userId: member.user_id,
                                  isActive: true,
                                })
                              }
                              disabled={toggleAccessMutation.isPending}
                            >
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm('Remove this coach from your team?')) {
                                removeMutation.mutate(member.user_id);
                              }
                            }}
                            disabled={removeMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Permissions Modal */}
      {permissionsCoach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Coach Permissions</h2>
                <p className="text-sm text-muted-foreground">{permissionsCoach.name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPermissionsCoach(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {permissionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-4">
                {(
                  [
                    { key: 'can_invite', label: 'Can Invite Members' },
                    { key: 'can_create_events', label: 'Can Create Events' },
                    { key: 'can_view_billing', label: 'Can View Billing' },
                    { key: 'can_manage_coaches', label: 'Can Manage Coaches' },
                  ] as const
                ).map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between">
                    <Label htmlFor={`perm-${key}`} className="cursor-pointer">
                      {label}
                    </Label>
                    <Switch
                      id={`perm-${key}`}
                      checked={localPermissions[key]}
                      onCheckedChange={(checked) =>
                        setLocalPermissions((prev) => ({ ...prev, [key]: checked }))
                      }
                    />
                  </div>
                ))}

                <div className="flex justify-end gap-2 pt-4 border-t border-border">
                  <Button type="button" variant="outline" onClick={() => setPermissionsCoach(null)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={updatePermissionsMutation.isPending}
                    onClick={() =>
                      updatePermissionsMutation.mutate({
                        memberId: permissionsCoach.id,
                        permissions: localPermissions,
                      })
                    }
                  >
                    {updatePermissionsMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Permissions
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Invite Coach</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowInviteModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email *</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="coach@example.com"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    setInviteErrors((errors) => removeFieldError(errors, 'email'));
                  }}
                  required
                  {...fieldErrorProps(inviteErrors, 'email')}
                />
                <FieldError errors={inviteErrors} field="email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-name">Name (optional)</Label>
                <Input
                  id="invite-name"
                  type="text"
                  placeholder="Coach Name"
                  value={inviteName}
                  onChange={(e) => {
                    setInviteName(e.target.value);
                    setInviteErrors((errors) => removeFieldError(errors, 'name'));
                  }}
                  {...fieldErrorProps(inviteErrors, 'name')}
                />
                <FieldError errors={inviteErrors} field="name" />
              </div>
              {/* FEAT-APR08-007 — gym picker for multi-gym owners */}
              {isMultiGymOwner && (
                <div className="space-y-2">
                  <Label htmlFor="invite-gym">Assign to gym</Label>
                  <select
                    id="invite-gym"
                    value={inviteAffiliateId}
                    onChange={(e) => setInviteAffiliateId(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {ownedGyms.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    The coach will be invited to this gym only. Invite them again from the other
                    gym's Team page to give them access there too.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={inviteMutation.isPending}>
                  {inviteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Invite
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function removeFieldError(errors: FieldErrors | undefined, field: string) {
  if (!errors) return undefined;
  const { [field]: _removed, ...remaining } = errors;
  return Object.keys(remaining).length ? remaining : undefined;
}
